import { CommonModule, TitleCasePipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MenuItem } from 'primeng/api';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MenuModule } from 'primeng/menu';
import { PanelMenuModule } from 'primeng/panelmenu';
import { PopoverComponent } from '../../../@core/components/ui/popover/popover.component';
import { RippleModule } from 'primeng/ripple';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TooltipModule } from 'primeng/tooltip';
import { SOCKET_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { ILinkDocument } from '../../../@core/models/model';
import { ToastService } from '../../../@core/services/toast.service';
import { SearchBarComponent } from '../../../@shared/components/search-bar/search-bar.component';
import { ScrollResetDirective } from '../../../@shared/directives/scroll-reset.directive';
import { CategorizeByDatePipe } from '../../../@shared/pipes/categorizeByDate-chat.pipe';
import { SanitizeHtmlPipe } from '../../../@shared/pipes/sanitizeHtml.pipe';
import { TruncatePipe } from "../../../@shared/pipes/truncate.pipe";
import { AssetUrlPipe } from '../../../@shared/pipes/asset-url.pipe';
import { SVG_ICONS } from '../../constants/constant';
import { ICompareKpi } from '../../models/settings.model';
import { AiChatService } from '../../services/aichat.service';
import { LinkDocumentService } from '../../services/link-document.service';
import { IConversation, IMessages, SENDER_TYPE } from './chat.types';
import { AiChatBubbleComponent } from './components/ai-chat-bubble/ai-chat-bubble.component';

export enum MESSAGE_STATUS {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
  IN_PROGRESS = 'IN_PROGRESS'
}

export enum ChatSource {
  CONTRACT = 'CONTRACT',
  COMPARE = 'COMPARE',
  DOCUMENT_FORM = 'DOCUMENT_FORM'
}

/**
 * Interface for AI chat response sent via socket
 */
export interface IAIChatResponse {
  updatedAt: Date;
  createdAt: Date;
  content: {
    type: string;
    message: string;
  };
  conversation: string;
  aiMessageId: string;
  type: string;
  author: {
    name: string;
  };
  status: MESSAGE_STATUS;
  meta: {
    web_results: { title: string; url: string; content: string }[];
  };
}


export interface IChatOptions {
  selectedTags: { label: string, id: string, type: string }[],
  selectedOptions: (MenuItem & { description: string; type: string })[]
  selectedQuestions: { label: string, value: string, data: any }[] | null
}
@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AiChatBubbleComponent,
    SanitizeHtmlPipe,
    RippleModule,
    PopoverComponent,
    TooltipModule,
    TruncatePipe,
    ButtonComponent,
    ScrollResetDirective,
    InputGroupModule,
    InputGroupAddonModule,
    PanelMenuModule,
    MenuModule,
    SearchBarComponent,
    TitleCasePipe,
    SearchBarComponent,
    TitleCasePipe,
    ToggleButtonModule,
    AssetUrlPipe,
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})
export class ChatComponent implements OnInit, OnChanges {
  @ViewChild('editableContent', { static: false }) editableContent!: ElementRef;
  @ViewChild('suggestionsPanel', { static: false }) suggestionsPanel!: PopoverComponent;

  @Input({ required: true }) contractId!: string;
  @Input() compareId!: string;
  @Input() tabFocused: boolean = false;
  @Input() compareDocumentIds!: { _id: string, docModel: string, name: string }[];
  @Input() docModel!: string
  @Input() suggestions!: string[]
  @Input() chatSource!: ChatSource;
  @Input() showHistory: boolean = false;
  @Input() showTags: boolean = false;
  @Input() showOptions: boolean = false;
  @Input() set compareResultKpi(value: { [key: string]: ICompareKpi }) {
    this._compareResultKpi = value;
    this.getCompareDocumentsAndKpis();
  }
  public _compareResultKpi!: { [key: string]: ICompareKpi };
  @Input() isEmptyDoc: boolean = false
  @Input() selectedQuestions: { label: string, value: string, data: any }[] | null = null;

  // Two-level Agent Mode state management:
  // 1. Global/Shared level - controls if agent mode feature is visible
  public agentModeFeatureVisible: boolean = false;

  // 2. Component/Transaction level - controls if agent mode is active for current transaction
  public agentModeEnabled: boolean = true;

  // Input to control feature visibility at component level (can be set from parent)
  @Input() set showAgentModeFeature(value: boolean) {
    this.agentModeFeatureVisible = value;
  }

  // Output to notify parent when agent mode state changes at transaction level
  @Output() agentModeChange = new EventEmitter<boolean>();

  // Unique identifier for this chat instance (for storing transaction-level state)
  private get chatInstanceId(): string {
    return `${this.chatSource}_${this.contractId || this.compareId}`;
  }

  public icons = SVG_ICONS;
  public maxPromptLength: number = 1000
  public promptinput: string = ''
  public promptLength: number = 0
  public showLimitWarning: boolean = false;
  public unfilteredConversationsHistory!: IConversation[]
  public conversationsHistory!: IConversation[][]
  public selectedConversation!: IConversation['_id']
  public promptQuestions = [
    'What are all the clauses in this document?',
    'How far is the term of the contract?',
    'What is dispute clauses and dispute resolution in this document?',
    'What are payment terms in this document?'
  ]
  public inputPrompt: string = ''
  public prompt: string = ''
  public showSuggestions: boolean = true;

  private conversationLoadingStates: Map<string, boolean> = new Map();
  private isCreatingNewConversation: boolean = false; // Tracks when we're creating a brand new conversation (before we have an ID)

  public MESSAGE_STATUS = MESSAGE_STATUS;
  public conversations: IMessages[] = [];

  public enableRename!: IConversation | null;
  public linkedDocuments: ILinkDocument[] = [];
  public showTagsPanel: boolean = false;
  public showOptionsPanel: boolean = false;
  public filteredLinkedDocuments: ILinkDocument[] = [];
  public identifiers: MenuItem[] = [];
  public options: (MenuItem & { description: string; type: string })[] = [
    {
      label: 'Table',
      icon: 'pi pi-table',
      description: 'Generate a table',
      type: 'TABLE',
      command: () => {

      }
    },
    {
      label: 'Graph',
      icon: 'pi pi-chart-bar',
      description: 'Generate a Graph',
      type: 'GRAPH',
      command: () => {

      }
    }
  ];
  public tags: { label: string, id: string, type: string }[] = []
  public selectedOptions: (MenuItem & { description: string; type: string })[] = []
  public filteredKpis!: ICompareKpi[];
  public filteredCompareDocuments: { _id: string, docModel: string, name: string }[] = []

  selectedIdentifier: MenuItem | null = null;
  searchText: string = '';

  public toggleOptions: { label: string, icon: string, value: string }[] = [
    {
      label: 'Web Search',
      icon: 'pi pi-globe',
      value: 'WEB_SEARCH'
    },
    {
      label: 'Knowledge Base',
      icon: 'pi pi-book',
      value: 'KNOWLEDGE_BASE'
    }
  ]

  selectedToggleOption: { label: string, icon: string, value: string }[] = [];

  public chatStarting: boolean = false;

  @ViewChild('scrollBottom', { static: true })
  private scrollBottom!: ElementRef;
  constructor(
    private aiChatbotService: AiChatService,
    private linkedDocumentService: LinkDocumentService,
    private toast: ToastService,
    private socketService: SocketService,
    private cd: ChangeDetectorRef,
    // private notificationService: NotificationService,
    @Optional() public dialogRef: MatDialogRef<ChatComponent>,
  ) {
    this.socketService.on<{ message: any }>(SOCKET_EVENTS.AI_CHAT).subscribe(res => {
      const data: IAIChatResponse = res[0] as unknown as IAIChatResponse;
      this.processSocketResponse(data);
    })
  }
  ngOnInit(): void {
    this.getIdentifiers()
    this.getSuggestions();
    this.getConversations();
  }

  get isCurrentConversationLoading(): boolean {
    if (this.isCreatingNewConversation) return true
    const hasInitiatedRequest = this.conversationLoadingStates.get(this.selectedConversation) ?? false;
    const hasMessageInProgress = this.conversations.some(message => message.status === MESSAGE_STATUS.IN_PROGRESS);
    return hasInitiatedRequest || hasMessageInProgress;
  }

  private setConversationLoading(conversationId: string, loading: boolean): void {
    if (loading) {
      this.conversationLoadingStates.set(conversationId, true);
    } else {
      this.conversationLoadingStates.delete(conversationId);
    }
  }


  /**
   * Toggle agent mode for current transaction
   */
  onAgentModeToggle(enabled: boolean): void {

    this.agentModeEnabled = true;
    this.agentModeChange.emit(enabled);
  }


  ngOnChanges(changes: SimpleChanges) {
    if ((changes['tabFocused'] && changes['tabFocused'].currentValue) ||
      (changes['compareId'] && changes['compareId'].currentValue)) {
      this.selectedConversation = ''
      this.conversations = []
      this.getSuggestions();
      this.getConversations();
    }
    if (changes['compareId']) {
      this.compareId = changes['compareId'].currentValue;
    }
  }


  getIdentifiers() {
    switch (this.chatSource) {
      case ChatSource.CONTRACT:
        this.getLinkedDocuments();
        break;
      case ChatSource.COMPARE:
        this.getCompareDocumentsAndKpis();
        break;
      case ChatSource.DOCUMENT_FORM:
        this.getLinkedDocuments();
        this.getFormQuestions();
    }
  }

  getCompareDocumentsAndKpis() {
    this.filteredCompareDocuments = this.compareDocumentIds;
    this.filteredKpis = Object.values(this._compareResultKpi ?? {});
    this.identifiers = [{
      label: "Compare Docs",
      icon: 'pi pi-file',
      items: [...(this.compareDocumentIds.map((doc) => {
        return { label: doc.name, command: () => this.selectTag(doc.name, doc._id ?? "", 'COMPARE_DOCS') }
      }))]
    }, {
      label: "KPIs",
      icon: 'pi pi-chart-bar',
      items: [...(this.filteredKpis.map((kpi) => {
        return { label: kpi?.kpiDoc.name, command: () => this.selectTag(kpi?.kpiDoc.name ?? '', kpi.kpiDoc._id ?? "", 'KPIS') }
      }))]
    }]
  }

  // TODO:
  getFormQuestions() {
    if (this.selectedQuestions && this.selectedQuestions.length > 0) {
      this.identifiers = [{
        label: "Form Questions",
        icon: 'pi pi-question',
        items: [...(this.selectedQuestions.map((selectedQuestion) => {
          return { label: selectedQuestion.label, icon: 'pi pi-file', command: () => this.selectTag(selectedQuestion.label, selectedQuestion.value, 'FORM_QUESTIONS') }
        }))]
      }]
      this.selectTag(this.selectedQuestions[0].label, this.selectedQuestions[0].value, 'FORM_QUESTIONS');
    }
  }

  selectIdentifier(identifier: MenuItem) {
    this.selectedIdentifier = identifier;
    this.searchText = '';
  }

  deselectIdentifier() {
    this.selectedIdentifier = null;
  }

  getFilteredItems(): MenuItem[] {
    if (!this.selectedIdentifier) return [];
    return this.selectedIdentifier?.items?.filter(item =>
      item?.label?.toLowerCase().includes(this.searchText.toLowerCase()) ?? []
    ) ?? [];
  }

  resetIdentifiers() {
    switch (this.chatSource) {
      case ChatSource.CONTRACT:
        this.filteredLinkedDocuments = this.linkedDocuments
        this.identifiers = [{
          label: "Linked Documents",
          icon: 'pi pi-link',
          items: [...(this.linkedDocuments.map((doc) => {
            return { label: doc.targetDocument.name, icon: 'pi pi-file', command: () => this.selectTag(doc.targetDocument.name, doc.targetDocument._id, 'LINKED_DOCS') }
          }))]
        }]
        break;
      case ChatSource.COMPARE:
        this.filteredCompareDocuments = this.compareDocumentIds;
        this.filteredKpis = Object.values(this._compareResultKpi ?? {});
        this.identifiers = [{
          label: "Compare Docs",
          icon: 'pi pi-file',
          items: [...(this.compareDocumentIds.map((doc) => {
            return { label: doc.name, command: () => this.selectTag(doc.name, doc._id ?? "", 'COMPARE_DOCS') }
          }))]
        }, {
          label: "KPIs",
          icon: 'pi pi-chart-bar',
          items: [...(this.filteredKpis.map((kpi) => {
            return { label: kpi?.kpiDoc.name, icon: 'pi pi-chart-bar', command: () => this.selectTag(kpi?.kpiDoc.name ?? '', kpi.kpiDoc._id ?? "", 'KPIS') }
          }))]
        }]
        break;
      case ChatSource.DOCUMENT_FORM:
        this.selectedQuestions = [];

    }
  }

  getLinkedDocuments() {
    this.linkedDocumentService.fetchLinkDocument(this.contractId).subscribe({
      next: res => {
        this.linkedDocuments = res.data.data;
        this.filteredLinkedDocuments = this.linkedDocuments;
        this.identifiers = [{
          label: "Linked Documents",
          icon: 'pi pi-link',
          items: [...(this.linkedDocuments.map((doc) => {
            return { label: doc.targetDocument.name, icon: 'pi pi-file', command: () => this.selectTag(doc.targetDocument.name, doc.targetDocument._id, 'LINKED_DOCS') }
          }))]
        }]
      }
    })
  }

  selectTag(label: string, tagId: string, type: string) {
    switch (type) {
      case 'LINKED_DOCS':
        this.prompt = this.prompt.replace('@', '');
        this.filteredLinkedDocuments = this.filteredLinkedDocuments.filter((doc) => doc.targetDocument._id !== tagId)
        this.identifiers = [{
          label: "Linked Documents",
          icon: 'pi pi-link',
          items: [...(this.filteredLinkedDocuments.map((doc) => {
            return { label: doc.targetDocument.name, icon: 'pi pi-file', command: () => this.selectTag(doc.targetDocument.name, doc.targetDocument._id, 'LINKED_DOCS') }
          }))]
        }]
        this.tags.push({ label, id: tagId, type })
        this.showTagsPanel = false;
        break;
      case 'KPIS':
        if (this.tags.length > 0) {
          this.toast.info('Only one KPI can be selected at a time');
          return;
        }
        this.prompt = this.prompt.replace('@', '');
        this.tags.push({ label, id: tagId, type })
        this.showTagsPanel = false;
        this.filteredKpis = (Object.values(this._compareResultKpi || [])).filter((kpi: any) => kpi.kpi._id !== tagId)
        this.identifiers[1] = {
          label: "KPIs",
          icon: 'pi pi-chart-bar',
          items: [...(this.filteredKpis.map((kpi) => {
            return { label: kpi.kpiDoc.name, icon: 'pi pi-chart-bar', command: () => this.selectTag(kpi.kpiDoc.name, kpi.kpiDoc._id ?? "", 'KPIS') }
          }))]
        }
        break;
      case 'COMPARE_DOCS':
        if (this.tags.length > 0) {
          this.toast.info('Only one Document can be selected at a time');
          return;
        }
        this.prompt = this.prompt.replace('@', '');
        this.tags.push({ label, id: tagId, type })
        this.showTagsPanel = false;
        this.filteredCompareDocuments = this.compareDocumentIds.filter((doc) => doc._id !== tagId)
        this.identifiers[0] = {
          label: "Compare Docs",
          icon: 'pi pi-file',
          items: [...(this.filteredCompareDocuments.map((doc) => {
            return { label: doc.name, icon: 'pi pi-file', command: () => this.selectTag(doc.name, doc._id ?? "", 'COMPARE_DOCS') }
          }))]
        }
        break;
      case 'FORM_QUESTIONS':
        this.prompt = this.prompt.replace('@', '');
        this.tags.push({ label, id: tagId, type })
        this.showTagsPanel = false;
        break;

    }
  }

  selectOption(option: MenuItem & { description: string, type: string }) {
    this.selectedOptions = [];
    this.selectedOptions.push(option);
    this.showOptionsPanel = false;
  }


  removeOption(option: MenuItem & { description: string, type: string }) {
    this.selectedOptions = this.selectedOptions?.filter((item) => item.label !== option.label)
  }

  removeTag(tag: { label: string, id: string, type: string }) {
    switch (this.chatSource) {
      case ChatSource.CONTRACT:
        const linkedDoc = this.linkedDocuments.find((doc) => doc.targetDocument._id === tag.id)
        if (linkedDoc) this.filteredLinkedDocuments = [...this.filteredLinkedDocuments, linkedDoc]
        this.tags = this.tags.filter((item) => item.id !== tag.id)
        this.identifiers = [{
          label: "Linked Documents",
          icon: 'pi pi-link',
          items: [...(this.filteredLinkedDocuments.map((doc) => {
            return { label: doc.targetDocument.name, icon: 'pi pi-file', command: () => this.selectTag(doc.targetDocument.name, doc.targetDocument._id, 'LINKED_DOCS') }
          }))]
        }]
        break;

      case ChatSource.COMPARE:
        if (tag.type === 'COMPARE_DOCS') {
          const compareDoc = this.compareDocumentIds.find((doc) => doc._id === tag.id)
          if (compareDoc) this.filteredCompareDocuments = [...this.filteredCompareDocuments, compareDoc]
        } else if (tag.type === 'KPIS') {
          const kpi = Object.values(this._compareResultKpi ?? {}).find((kpi) => kpi.kpi === tag.id)
          if (kpi) this.filteredKpis = [...this.filteredKpis, kpi]
        }
        this.tags = this.tags.filter((item) => item.id !== tag.id)
        this.identifiers = [{
          label: "Compare Docs",
          icon: 'pi pi-file',
          items: [...(this.filteredCompareDocuments.map((doc) => {
            return { label: doc.name, icon: 'pi pi-file', command: () => this.selectTag(doc.name, doc._id ?? "", 'COMPARE_DOCS') }
          }))]
        }, {
          label: "KPIs",
          icon: 'pi pi-chart-bar',
          items: [...(this.filteredKpis.map((kpi) => {
            return { label: kpi.kpiDoc.name, icon: 'pi pi-chart-bar', command: () => this.selectTag(kpi.kpiDoc.name, kpi.kpiDoc._id ?? "", 'KPIS') }
          }))]
        }]
        break;

      case ChatSource.DOCUMENT_FORM:
        if (this.selectedQuestions) {
          this.selectedQuestions = this.selectedQuestions.filter((item) => item.value !== tag.id)
          this.tags = this.tags.filter((item) => item.id !== tag.id)
          this.identifiers = [{
            label: "Form Questions",
            icon: 'pi pi-question',
            items: [...(this.selectedQuestions.map((selectedQuestion) => {
              return { label: selectedQuestion.label, icon: 'pi pi-file', command: () => this.selectTag(selectedQuestion.label, selectedQuestion.value, 'FORM_QUESTIONS') }
            }))]
          }]
        }
        break;
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    const { key } = event;
    if (key === '@') {
      this.showTagsPanel = true;
    }
    else {
      this.showTagsPanel = false;
    }
  }

  getSuggestions() {
    this.aiChatbotService.getPromptSuggestions(this.chatSource == ChatSource.COMPARE ? this.compareId : this.contractId, this.chatSource).subscribe({
      next: res => {
        this.promptQuestions = (res.data && res.data.length) ? res.data : (this.suggestions ?? this.promptQuestions);
      },
      error: _ => {
        this.promptQuestions = this.suggestions ?? this.promptQuestions
      }
    })
  }

  processSocketResponse(data: IAIChatResponse) {
    if (this.selectedConversation === data.conversation) {
      const index = this.conversations.findIndex((message) => message._id === data.aiMessageId);
      if (index !== -1) {
        this.conversations[index].message = data.content.message ?? '';
        this.conversations[index].meta = data.meta;
        this.conversations[index].status = data.status as MESSAGE_STATUS;
        this.conversations[index].updatedAt = data.updatedAt;
        if (data.status === MESSAGE_STATUS.COMPLETED) {
          this.setConversationLoading(data.conversation, false);
          this.getConversations()
          this.selectedConversation = data.conversation;
          this.tags = [];
        }
        else if (data.status === MESSAGE_STATUS.ERROR) {
          this.scrollToBottom();
          this.conversations[index].message = 'Something went wrong, please try again';
          this.setConversationLoading(data.conversation, false);
          this.tags = [];
        } else {
          this.scrollToBottom();
        }
      }
    } else {
      if (this.conversationsHistory.find((conversation) => conversation.find((item) => item._id === data.conversation))) {
        if (data.status === MESSAGE_STATUS.COMPLETED || data.status === MESSAGE_STATUS.ERROR) {
          this.setConversationLoading(data.conversation, false);
        }
      }
    }
  }
  getResponse(value: string, selected_tags: { label: string, id: string, type: string }[], selected_options: (MenuItem & { description: string; type: string })[], selected_toggle_option: { label: string, icon: string, value: string }[]) {
    this.tags = [];
    this.selectedOptions = [];
    this.showSuggestions = false;
    if (this.conversations.length > 60) this.showLimitWarning = true;
    this.chatStarting = true;
    this.agentModeEnabled = true;

    if (this.selectedConversation) {
      this.setConversationLoading(this.selectedConversation, true);
    } else {
      this.isCreatingNewConversation = true;
    }

    this.aiChatbotService.sendMessage(this.chatSource, this.agentModeEnabled, value, this.contractId, this.compareId, this.selectedConversation, this.compareDocumentIds, this.docModel, selected_tags, selected_options, selected_toggle_option).subscribe({
      next: (res: any) => {
        if (res && res.success) {
          const newConversation = res.data.conversation;
          if (newConversation._id !== this.selectedConversation && !this.conversationsHistory.some((conversation) => conversation.find((item) => item._id === newConversation._id))) {
            this.unfilteredConversationsHistory = [newConversation, ...this.unfilteredConversationsHistory];
            this.conversationsHistory = new CategorizeByDatePipe().transform(this.unfilteredConversationsHistory);
          }

          this.isCreatingNewConversation = false;
          this.selectedConversation = newConversation._id;
          this.setConversationLoading(newConversation._id, true);

          const newMessages = res.data.newMessages;
          this.conversations = [
            ...this.conversations,
            ...newMessages
          ]
          this.chatStarting = false;
          this.cd.detectChanges();
        }
      },
      error: () => {
        this.isCreatingNewConversation = false;
        if (this.selectedConversation) {
          this.setConversationLoading(this.selectedConversation, false);
        }
        this.chatStarting = false;
      }
    })
  }

  checkPromptLength() {
    this.promptLength = this.prompt.length;
  }


  scrollToBottom(): void {
    try {
      setTimeout(() => {
        this.scrollBottom.nativeElement.scrollTop = this.scrollBottom.nativeElement.scrollHeight;
      }, 100)
    } catch (err) {
    }
  }


  sendPrompt() {
    if (this.isCurrentConversationLoading) return;
    if (this.prompt.length > 0) {
      this.showTagsPanel = false;
      this.promptinput = this.prompt;
      this.prompt = '';
      this.checkPromptLength();
      this.getResponse(this.promptinput, this.tags, this.selectedOptions, this.selectedToggleOption);
      this.scrollToBottom();
      this.resetIdentifiers();
    }
  }

  getConversations() {
    switch (this.chatSource) {
      case ChatSource.CONTRACT:
      case ChatSource.DOCUMENT_FORM:
        if (!this.contractId) return;
        this.aiChatbotService.getAllConversations(this.contractId).subscribe((res: any) => {
          if (res && res.data) {
            this.unfilteredConversationsHistory = res.data;
            this.conversationsHistory = new CategorizeByDatePipe().transform(res.data);
          }
        })
        break;
      case ChatSource.COMPARE:
        if (!this.compareId) return;
        this.aiChatbotService.getAllConversations(this.compareId).subscribe((res: any) => {
          if (res && res.data) {
            this.unfilteredConversationsHistory = res.data;
            this.conversationsHistory = new CategorizeByDatePipe().transform(res.data);
          }
        })
        break;



    }

  }

  getMessages(conversation: string, historyPanel: any, event: Event) {
    if (historyPanel.overlayVisible) {
      historyPanel.toggle(event);
    }
    this.selectedConversation = conversation;
    this.aiChatbotService.getConversation(conversation).subscribe((res) => {
      if (res && res.data) {
        this.conversations = [...res.data]
        this.showSuggestions = false;
        this.scrollToBottom();
        this.setToggleOptionsFromLastUserMessage();
      }
    })
  }

  private setToggleOptionsFromLastUserMessage(): void {
    // Find the last USER message in the conversation and set the toggle options based on the meta data
    const lastUserMessage = [...this.conversations]
      .reverse()
      .find((message) => message.senderType === SENDER_TYPE.USER);

    if (lastUserMessage && lastUserMessage.meta && lastUserMessage.meta.selected_toggle_options) {
      const selectedValues = lastUserMessage.meta.selected_toggle_options;

      if (Array.isArray(selectedValues) && selectedValues.length > 0) {
        if (typeof selectedValues[0] === 'string') {
          this.selectedToggleOption = this.toggleOptions.filter((option) =>
            (selectedValues as string[]).includes(option.value)
          );
        } else {
          this.selectedToggleOption = selectedValues as { label: string, icon: string, value: string }[];
        }
      }
    } else {
      // Clear selected toggles if no data is available
      this.selectedToggleOption = [];
    }
  }

  startNewConversation() {
    this.showSuggestions = true;
    this.conversations = [];
    this.selectedConversation = '';
    this.selectedToggleOption = [];
  }

  updateConversationName(event: Event, conversation: IConversation) {
    event.preventDefault()
    const target = event.target as HTMLElement;
    const newName = target.textContent?.trim() || 'Untitled';
    target.innerHTML = newName
    if (newName && this.enableRename && conversation.name !== newName) {
      this.aiChatbotService.updateConversationName(conversation._id, newName).subscribe((res) => {
        if (res) {
          this.toast.success('Conversation name updated successfully');
          this.enableRename = null;
          this.getConversations();
        }
      })
    } else {
      this.enableRename = null;
    }
  }

  renameChatHistory(conversation: IConversation) {
    this.enableRename = conversation
  }

  blurSpan(event: Event) {
    const target = event.target as HTMLElement;
    target.blur();
  }
  searchConversations(event: Event) {
    if ((event.target as HTMLInputElement).value.toLowerCase() == "") {
      this.conversationsHistory = new CategorizeByDatePipe().transform(this.unfilteredConversationsHistory);
      return;
    }
    const filteredConversations = this.unfilteredConversationsHistory.filter((conversation) => conversation.name.toLowerCase().includes((event.target as HTMLInputElement).value.toLowerCase()));
    if (filteredConversations) {
      this.conversationsHistory = new CategorizeByDatePipe().transform(filteredConversations);
    } else {
      this.conversationsHistory = new CategorizeByDatePipe().transform(this.unfilteredConversationsHistory);
    }
  }

  deleteConversation(conversation: IConversation) {
    this.aiChatbotService.deleteConversation(conversation._id).subscribe((res) => {
      if (res) {
        this.toast.success('Conversation deleted successfully');
        if (this.selectedConversation === conversation._id) {
          this.startNewConversation();
        }
        this.getConversations();
      }
    })

  }

  removeSelectedItem(item: { label: string, value: string, data: any }) {
    if (this.selectedQuestions) {
      this.selectedQuestions = this.selectedQuestions.filter((i) => i.value !== item.value);
      this.scrollToBottom();
    }
  }

  selectToggleOption(option: { label: string, icon: string, value: string }) {
    if (this.selectedToggleOption.includes(option)) {
      this.selectedToggleOption = this.selectedToggleOption.filter((o) => o.value !== option.value)
    } else {
      this.selectedToggleOption.push(option);
    }
  }



}
